/**
 * Album page formats. All sizes are the TRIM size of ONE page in millimetres.
 * A spread is two pages side by side (2 × widthMm). Bleed is added only at export.
 */
export type FormatId =
  | 'sq15' | 'sq20' | 'sq25' | 'sq30'
  | 'a4p' | 'a4l'
  | 'r20x30' | 'r30x20' | 'r25x35' | 'r40x30'
  | 'in85sq' | 'in10sq' | 'in12sq' | 'in8x10' | 'in85x11' | 'in11x85' | 'in14x11'
  | 'custom';

/** How the wizard groups the presets: the metric sizes, and the inch sizes print services sell. */
export type FormatGroup = 'metric' | 'inch';

export interface FormatPreset {
  id: FormatId;
  widthMm: number;
  heightMm: number;
  /** i18n key under `formats.*` */
  nameKey: string;
  /** rough family used to pick layouts: square | portrait | landscape | panoramic */
  family: 'square' | 'portrait' | 'landscape' | 'panoramic';
  group: FormatGroup;
}

const IN = 25.4;
/** inches → mm to a tenth, so 8.5 in is 215.9 mm and a file at that size needs no scaling at the shop */
const inch = (n: number): number => Math.round(n * IN * 10) / 10;

export const FORMAT_PRESETS: Readonly<Record<Exclude<FormatId, 'custom'>, FormatPreset>> = {
  sq15:   { id: 'sq15',   widthMm: 150, heightMm: 150, nameKey: 'formats.sq15',   family: 'square',    group: 'metric' },
  sq20:   { id: 'sq20',   widthMm: 200, heightMm: 200, nameKey: 'formats.sq20',   family: 'square',    group: 'metric' },
  sq25:   { id: 'sq25',   widthMm: 250, heightMm: 250, nameKey: 'formats.sq25',   family: 'square',    group: 'metric' },
  sq30:   { id: 'sq30',   widthMm: 300, heightMm: 300, nameKey: 'formats.sq30',   family: 'square',    group: 'metric' },
  a4p:    { id: 'a4p',    widthMm: 210, heightMm: 297, nameKey: 'formats.a4p',    family: 'portrait',  group: 'metric' },
  a4l:    { id: 'a4l',    widthMm: 297, heightMm: 210, nameKey: 'formats.a4l',    family: 'landscape', group: 'metric' },
  r20x30: { id: 'r20x30', widthMm: 200, heightMm: 300, nameKey: 'formats.r20x30', family: 'portrait',  group: 'metric' },
  r30x20: { id: 'r30x20', widthMm: 300, heightMm: 200, nameKey: 'formats.r30x20', family: 'landscape', group: 'metric' },
  r25x35: { id: 'r25x35', widthMm: 250, heightMm: 350, nameKey: 'formats.r25x35', family: 'portrait',  group: 'metric' },
  r40x30: { id: 'r40x30', widthMm: 400, heightMm: 300, nameKey: 'formats.r40x30', family: 'landscape', group: 'metric' },
  // the sizes PrestoPhoto (docs/00-decisions.md — "Print services") sells most; exact, so nothing is scaled
  in85sq: { id: 'in85sq', widthMm: inch(8.5), heightMm: inch(8.5), nameKey: 'formats.in85sq', family: 'square',    group: 'inch' },
  in10sq: { id: 'in10sq', widthMm: inch(10),  heightMm: inch(10),  nameKey: 'formats.in10sq', family: 'square',    group: 'inch' },
  in12sq: { id: 'in12sq', widthMm: inch(12),  heightMm: inch(12),  nameKey: 'formats.in12sq', family: 'square',    group: 'inch' },
  in8x10: { id: 'in8x10', widthMm: inch(8),   heightMm: inch(10),  nameKey: 'formats.in8x10', family: 'portrait',  group: 'inch' },
  in85x11:{ id: 'in85x11',widthMm: inch(8.5), heightMm: inch(11),  nameKey: 'formats.in85x11',family: 'portrait',  group: 'inch' },
  in11x85:{ id: 'in11x85',widthMm: inch(11),  heightMm: inch(8.5), nameKey: 'formats.in11x85',family: 'landscape', group: 'inch' },
  in14x11:{ id: 'in14x11',widthMm: inch(14),  heightMm: inch(11),  nameKey: 'formats.in14x11',family: 'landscape', group: 'inch' },
};

export const FORMAT_GROUPS: readonly FormatGroup[] = ['metric', 'inch'];

export const CUSTOM_SIZE_LIMITS = { minMm: 100, maxMm: 450 } as const;
export const PAGE_COUNT_LIMITS = { min: 10, max: 120 } as const;
export const DEFAULT_BLEED_MM = 3;
export const EXPORT_DPI_LIMITS = { min: 300, max: 1200, default: 300 } as const;

export function familyOf(widthMm: number, heightMm: number): FormatPreset['family'] {
  const r = widthMm / heightMm;
  if (Math.abs(r - 1) < 0.05) return 'square';
  if (r > 1.6) return 'panoramic';
  return r > 1 ? 'landscape' : 'portrait';
}

/** Default hard-cover spine width. Overridable per album. */
export function defaultSpineWidthMm(innerPageCount: number): number {
  return Math.max(4, Math.round(innerPageCount * 0.12 + 2));
}
